import { DEFAULT_HIDDEN_QUEST_CODES } from "common/constants";
import type {
  AdminQuest,
  QuestTabKey,
} from "common/types/questManagement.type";

const QUEST_SAVE_FALLBACK_MESSAGE =
  "クエストの保存に失敗しました。入力内容を確認してください。";

const includesJapaneseText = (value: string): boolean =>
  /[\u3040-\u30ff\u3400-\u9fff]/.test(value);

export const getRequestErrorMessage = (error: unknown, fallback: string) => {
  const extractMessage = (payload: unknown): string | null => {
    if (
      typeof payload === "object" &&
      payload !== null &&
      "message" in payload
    ) {
      const message = (payload as { message?: unknown }).message;
      if (typeof message === "string") return message;
      if (Array.isArray(message)) return message.join("\n");
    }

    return null;
  };

  if (typeof error === "object" && error !== null && "data" in error) {
    const message = extractMessage((error as { data?: unknown }).data);
    if (message) return message;
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof (error as { response?: unknown }).response === "object" &&
    (error as { response?: unknown }).response !== null
  ) {
    const responseData = (error as { response: { data?: unknown } }).response
      .data;
    const message = extractMessage(responseData);
    if (message) return message;
  }

  return fallback;
};

export const getPaginationItems = (
  currentPage: number,
  totalPages: number,
): (number | "...")[] => {
  if (totalPages <= 1) return [];
  if (totalPages <= 4) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }
  if (currentPage <= 2) return [1, 2, 3, "...", totalPages];
  if (currentPage >= totalPages - 1) {
    return [1, "...", totalPages - 2, totalPages - 1, totalPages];
  }
  return [1, "...", currentPage, "...", totalPages];
};

export const getQuestCategory = (quest: AdminQuest): QuestTabKey =>
  quest.quest_type === "hidden" || quest.is_hidden ? "hidden" : "monthly";

export const isDefaultHiddenQuest = (quest: AdminQuest | null): boolean => {
  return Boolean(
    quest?.is_hidden && DEFAULT_HIDDEN_QUEST_CODES.includes(quest.code),
  );
};

export const formatQuestActiveMonth = (
  value: string | null | undefined,
): string => {
  if (!value) return "無期限";

  const [year, month] = value.slice(0, 7).split("-");

  if (!year || !month) return "無期限";

  return `${year}年${month.padStart(2, "0")}月`;
};

export const formatQuestActivePeriod = ({
  active_end_at,
  active_start_at,
}: {
  active_start_at?: string | null;
  active_end_at?: string | null;
}): string => {
  if (!active_start_at && !active_end_at) {
    return "※有効期限：無期限";
  }

  const start = active_start_at ? formatQuestActiveMonth(active_start_at) : "";
  const end = active_end_at ? formatQuestActiveMonth(active_end_at) : "";

  return `※有効期限：${start}～${end}`;
};

export const getQuestSaveErrorMessage = (error: unknown): string => {
  const message = getRequestErrorMessage(error, QUEST_SAVE_FALLBACK_MESSAGE);

  if (/Quest with code ".+" already exists/.test(message)) {
    return "同じクエストコードが既に存在します。再度お試しください。";
  }

  if (/already exists/i.test(message)) {
    return "既に同じ内容のクエストが存在します。入力内容を確認してください。";
  }

  if (
    message === QUEST_SAVE_FALLBACK_MESSAGE ||
    !includesJapaneseText(message)
  ) {
    return QUEST_SAVE_FALLBACK_MESSAGE;
  }

  return message;
};
